import ToasterUi from 'toaster-ui';

// Create a singleton instance
const toaster = new ToasterUi();

// Custom toast configuration
const toastConfig = {
  duration: 4000,
  autoClose: true,
  allowHtml: true,
};

/** Suppress identical toasts from socket + local success (or double listeners). */
const TOAST_DEDUPE_MS = 2500;
const lastToastByType: Record<string, { text: string; at: number }> = {};

function shouldSkipDuplicateToast(type: string, message: string): boolean {
  const text = String(message || '').trim();
  if (!text) return true;
  const now = Date.now();
  const prev = lastToastByType[type];
  if (prev && prev.text === text && now - prev.at < TOAST_DEDUPE_MS) {
    return true;
  }
  lastToastByType[type] = { text, at: now };
  return false;
}

// Professional success toast with icon
export const showSuccessToast = (message: string) => {
  if (shouldSkipDuplicateToast('success', message)) return;

  const successIcon = `
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-right: 12px; flex-shrink: 0;">
      <path d="M16.7071 5.29289C17.0976 5.68342 17.0976 6.31658 16.7071 6.70711L8.70711 14.7071C8.31658 15.0976 7.68342 15.0976 7.29289 14.7071L3.29289 10.7071C2.90237 10.3166 2.90237 9.68342 3.29289 9.29289C3.68342 8.90237 4.31658 8.90237 4.70711 9.29289L8 12.5858L15.2929 5.29289C15.6834 4.90237 16.3166 4.90237 16.7071 5.29289Z" fill="#10b981"/>
    </svg>
  `;

  toaster.addToast(
    `<div style="display: flex; align-items: center; width: 100%;">
      ${successIcon}
      <span style="flex: 1; line-height: 1.5;">${message}</span>
    </div>`,
    'success',
    {
      ...toastConfig,
      styles: {
        backgroundColor: '#ffffff',
        color: '#065f46',
        border: '1px solid #d1fae5',
        borderLeft: '4px solid #10b981',
        borderRadius: '8px',
        padding: '16px 20px',
        boxShadow:
          '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        fontSize: '14px',
        fontWeight: '500',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        minWidth: '320px',
        maxWidth: '420px',
        lineHeight: '1.5',
        letterSpacing: '-0.01em',
      },
    },
  );
};

// Professional error toast with icon
export const showErrorToast = (message: string) => {
  if (shouldSkipDuplicateToast('error', message)) return;

  const errorIcon = `
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-right: 12px; flex-shrink: 0;">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18ZM8.70711 7.29289C8.31658 6.90237 7.68342 6.90237 7.29289 7.29289C6.90237 7.68342 6.90237 8.31658 7.29289 8.70711L8.58579 10L7.29289 11.2929C6.90237 11.6834 6.90237 12.3166 7.29289 12.7071C7.68342 13.0976 8.31658 13.0976 8.70711 12.7071L10 11.4142L11.2929 12.7071C11.6834 13.0976 12.3166 13.0976 12.7071 12.7071C13.0976 12.3166 13.0976 11.6834 12.7071 11.2929L11.4142 10L12.7071 8.70711C13.0976 8.31658 13.0976 7.68342 12.7071 7.29289C11.6834 6.90237 11.3166 6.90237 10.7071 7.29289L10 8L9.29289 7.29289C8.90237 6.90237 8.31658 6.90237 8.70711 7.29289Z" fill="#ef4444"/>
    </svg>
  `;

  toaster.addToast(
    `<div style="display: flex; align-items: center; width: 100%;">
      ${errorIcon}
      <span style="flex: 1; line-height: 1.5;">${message}</span>
    </div>`,
    'error',
    {
      ...toastConfig,
      duration: Math.max(12000, Math.min(25000, message.length * 80)),
      styles: {
        backgroundColor: '#ffffff',
        color: '#991b1b',
        border: '1px solid #fee2e2',
        borderLeft: '4px solid #ef4444',
        borderRadius: '8px',
        padding: '16px 20px',
        boxShadow:
          '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        fontSize: '14px',
        fontWeight: '500',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        minWidth: '320px',
        maxWidth: '500px',
        lineHeight: '1.5',
        letterSpacing: '-0.01em',
      },
    },
  );
};

// Professional info toast with icon
export const showInfoToast = (message: string) => {
  if (shouldSkipDuplicateToast('info', message)) return;

  const infoIcon = `
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-right: 12px; flex-shrink: 0;">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10ZM11 6C11 6.55228 10.5523 7 10 7C9.44772 7 9 6.55228 9 6C9 5.44772 9.44772 5 10 5C10.5523 5 11 5.44772 11 6ZM9 9C8.44772 9 8 9.44772 8 10C8 10.5523 8.44772 11 9 11V14C9 14.5523 9.44772 15 10 15C10.5523 15 11 14.5523 11 14V11C11 10.4477 10.5523 10 10 10H9Z" fill="#3b82f6"/>
    </svg>
  `;

  toaster.addToast(
    `<div style="display: flex; align-items: center; width: 100%;">
      ${infoIcon}
      <span style="flex: 1; line-height: 1.5;">${message}</span>
    </div>`,
    'info',
    {
      ...toastConfig,
      styles: {
        backgroundColor: '#ffffff',
        color: '#1e40af',
        border: '1px solid #dbeafe',
        borderLeft: '4px solid #3b82f6',
        borderRadius: '8px',
        padding: '16px 20px',
        boxShadow:
          '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        fontSize: '14px',
        fontWeight: '500',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        minWidth: '320px',
        maxWidth: '420px',
        lineHeight: '1.5',
        letterSpacing: '-0.01em',
      },
    },
  );
};
